import { useEffect, useState } from "react";
import MobileFrame from "../../components/MobileFrame";
import {
    ChevronLeft,
    Info,
    FileText,
    ExternalLink,
    Calculator,
    ListChecks,
} from "lucide-react";

import {
    getCurrentOrder,
    getOrderKfs,
} from "../../api/journey.api";

import type {
    CurrentOrderResponse,
} from "../../api/journey.api";

interface LoanSummaryProps {
    onBack: () => void;
    onContinue?: () => void;
    entryPoint?: "dashboard" | "mandate";
    kfsUrl?: string;
}

interface LoanCalculations {
    loanAmount: number;
    grossTenure: number;
    advancedEMI: number;
    NoOfNetEmis: number;
    netLoanAmount: number;
    customerROI: number;
    totalProcessingFee: number;
    monthlyEMI: number;
    totalInterest: number;
    totalDownPayment: number;
    advancedEmiPayment: number;
    DBDToDealer: number;
    DealerGets: number;
    netDisbursement: number;
    name: string;
    emiStartDate?: string;
}

interface OrderData {
    orderId: string;
    amount: number;
    lender?: string;
    status?: string;
    createdAt?: string;

    productDetails?: string;
    productCategory?: string;
    productBrand?: string;

    keyFactStatement?: {
        amount: number;

        scheme?: {
            _id?: string;
            name: string;
            grossTenure: number;
            advancedEMI: number;
            subventionCost: number;
            interestBased: boolean;
            annualInterest: number;
            processingRate: number;
            GST: number;
            schemeId: string;
        };

        calculations?: LoanCalculations;
    };

    settlement?: {
        customerName?: string;
        customerMobileNo?: string;

        orderId?: string;

        schemeName?: string;
        schemeTenure?: number;
        schemeEMIAmount?: number;

        totalAmount?: number;

        productName?: string;
        productCategory?: string;
        productBrand?: string;

        partnerName?: string;
        partnerMobileNo?: string;

        schemeDownPayment?: number;

        paymentFrequency?: string;

        netDisbursement?: number;

        interestAmount?: number;
        emiStartDate?: string;
    };
}

const formatINR = (value: number) =>
    `₹${value.toLocaleString("en-IN", {
        maximumFractionDigits: 0,
    })}`;

const ordinal = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;

    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const emiDueDateLabel = (baseDate: Date, index: number) => {
    const d = new Date(baseDate);

    d.setMonth(d.getMonth() + index);

    return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

/*
 * =====================================================
 * KFS BUILDER — produces a plain HTML string that gets
 * written into a real browser popup window (window.open),
 * not an in-app overlay. Sized like a small popup/new tab.
 * =====================================================
 */

const kfsRowHtml = (
    label: string,
    value: string,
    highlight = false
) => `
    <div class="
        kfs-row
        ${highlight ? "kfs-row-highlight" : ""}
    ">
        <span class="kfs-label">
            ${label}
        </span>

        <span class="kfs-value">
            ${value}
        </span>
    </div>
`;


const buildKfsHtml = (
    data: OrderData
) => {

    const c =
        data.keyFactStatement?.calculations;

    const s =
        data.settlement;


    /*
     * ============================================================
     * NO KFS
     * ============================================================
     */

    if (!c) {

        return `
            <!doctype html>

            <html>

                <head>

                    <title>
                        Order Summary
                    </title>

                    <meta
                        name="viewport"
                        content="
                            width=device-width,
                            initial-scale=1
                        "
                    />

                </head>

                <body
                    style="
                        margin:0;
                        min-height:100vh;
                        display:flex;
                        align-items:center;
                        justify-content:center;
                        font-family:
                            -apple-system,
                            BlinkMacSystemFont,
                            'Segoe UI',
                            Arial,
                            sans-serif;
                        background:#f8fafc;
                        color:#64748b;
                    "
                >

                    <div
                        style="
                            text-align:center;
                            padding:30px;
                        "
                    >

                        <div
                            style="
                                width:48px;
                                height:48px;
                                margin:0 auto 14px;
                                border-radius:14px;
                                background:#eef2ff;
                                display:flex;
                                align-items:center;
                                justify-content:center;
                                color:#6366f1;
                                font-size:22px;
                            "
                        >
                            !
                        </div>

                        <p
                            style="
                                margin:0;
                                font-size:14px;
                                font-weight:600;
                            "
                        >
                            KFS details are unavailable.
                        </p>

                    </div>

                </body>

            </html>
        `;

    }


    /*
     * ============================================================
     * ISSUE DATE
     * ============================================================
     */

    const issueDate =
        data.createdAt
            ? new Date(
                data.createdAt
            ).toLocaleDateString(
                "en-GB",
                {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                }
            )
            : "-";


    /*
     * ============================================================
     * TOTAL REPAYABLE
     * ============================================================
     *
     * Net Loan Amount
     * + Interest
     * + Processing Fee
     */

    const totalRepayableAmount =
        c.netLoanAmount +
        c.totalInterest +
        c.totalProcessingFee;


    return `

        <!doctype html>

        <html>

            <head>

                <meta charset="utf-8" />

                <meta
                    name="viewport"
                    content="
                        width=device-width,
                        initial-scale=1
                    "
                />


                <title>
                    Order Summary -
                    Order #${data.orderId}
                </title>


                <style>

                    * {
                        box-sizing:border-box;
                    }


                    html {
                        background:#f8fafc;
                    }


                    body {

                        margin:0;

                        font-family:
                            -apple-system,
                            BlinkMacSystemFont,
                            "Segoe UI",
                            Arial,
                            sans-serif;

                        color:#0f172a;

                        background:
                            linear-gradient(
                                180deg,
                                #eef2ff 0px,
                                #f8fafc 220px
                            );

                        -webkit-font-smoothing:
                            antialiased;

                    }


                    /*
                     * =================================================
                     * PAGE
                     * =================================================
                     */

                    .page {

                        width:100%;

                        max-width:680px;

                        margin:0 auto;

                        padding:
                            18px
                            16px
                            40px;

                    }


                    /*
                     * =================================================
                     * HEADER
                     * =================================================
                     */

                    .header {

                        padding:
                            8px
                            4px
                            20px;

                    }


                    .header-top {

                        display:flex;

                        align-items:center;

                        justify-content:
                            space-between;

                        gap:12px;

                    }


                    .tag {

                        display:inline-flex;

                        align-items:center;

                        padding:
                            5px
                            10px;

                        border-radius:
                            999px;

                        background:
                            #e0e7ff;

                        color:
                            #4f46e5;

                        font-size:
                            10px;

                        font-weight:
                            700;

                        letter-spacing:
                            .04em;

                        text-transform:
                            uppercase;

                    }


                    .order-id {

                        margin-top:
                            12px;

                        font-size:
                            19px;

                        font-weight:
                            750;

                        color:
                            #0f172a;

                        word-break:
                            break-word;

                    }


                    .issued {

                        margin:
                            5px
                            0
                            0;

                        font-size:
                            11px;

                        color:
                            #64748b;

                    }


                    /*
                     * =================================================
                     * SUMMARY
                     * =================================================
                     */

                    .summary-grid {

                        display:grid;

                        grid-template-columns:
                            repeat(
                                2,
                                minmax(0,1fr)
                            );

                        gap:
                            10px;

                        margin-bottom:
                            22px;

                    }


                    .summary-card {

                        padding:
                            13px;

                        background:
                            #ffffff;

                        border:
                            1px solid
                            #e2e8f0;

                        border-radius:
                            14px;

                        box-shadow:
                            0 1px 2px
                            rgba(
                                15,
                                23,
                                42,
                                .03
                            );

                        min-width:0;

                    }


                    .summary-label {

                        margin:0;

                        font-size:
                            9px;

                        font-weight:
                            700;

                        text-transform:
                            uppercase;

                        letter-spacing:
                            .05em;

                        color:
                            #94a3b8;

                    }


                    .summary-value {

                        margin:
                            5px
                            0
                            0;

                        font-size:
                            13px;

                        line-height:
                            1.4;

                        font-weight:
                            700;

                        color:
                            #1e293b;

                        word-break:
                            break-word;

                    }


                    /*
                     * =================================================
                     * SECTION TITLE
                     * =================================================
                     */

                    .section-title {

                        margin:
                            22px
                            0
                            8px;

                        padding:
                            0
                            4px;

                        font-size:
                            10px;

                        font-weight:
                            800;

                        letter-spacing:
                            .06em;

                        text-transform:
                            uppercase;

                        color:
                            #64748b;

                    }


                    /*
                     * =================================================
                     * SECTION
                     * =================================================
                     */

                    .section {

                        overflow:hidden;

                        background:
                            #ffffff;

                        border:
                            1px solid
                            #e2e8f0;

                        border-radius:
                            16px;

                        box-shadow:
                            0 1px 2px
                            rgba(
                                15,
                                23,
                                42,
                                .025
                            );

                    }


                    /*
                     * =================================================
                     * ROW
                     * =================================================
                     */

                    .kfs-row {

                        display:flex;

                        align-items:
                            flex-start;

                        justify-content:
                            space-between;

                        gap:
                            18px;

                        padding:
                            12px
                            14px;

                        border-bottom:
                            1px solid
                            #f1f5f9;

                    }


                    .kfs-row:last-child {

                        border-bottom:
                            none;

                    }


                    .kfs-label {

                        flex:
                            1;

                        min-width:
                            0;

                        font-size:
                            11px;

                        line-height:
                            1.45;

                        color:
                            #64748b;

                    }


                    .kfs-value {

                        max-width:
                            58%;

                        text-align:
                            right;

                        font-size:
                            11.5px;

                        line-height:
                            1.45;

                        font-weight:
                            700;

                        color:
                            #1e293b;

                        word-break:
                            break-word;

                    }


                    /*
                     * =================================================
                     * HIGHLIGHT ROW
                     * =================================================
                     */

                    .kfs-row-highlight {

                        background:
                            #f8faff;

                    }


                    .kfs-row-highlight
                    .kfs-label {

                        color:
                            #4f46e5;

                        font-weight:
                            700;

                    }


                    .kfs-row-highlight
                    .kfs-value {

                        color:
                            #4f46e5;

                        font-size:
                            13px;

                        font-weight:
                            800;

                    }


                    /*
                     * =================================================
                     * EMI CARD
                     * =================================================
                     */

                    .emi-card {

                        margin-top:
                            14px;

                        padding:
                            15px;

                        border-radius:
                            16px;

                        border:
                            1px solid
                            #c7d2fe;

                        background:
                            #eef2ff;

                    }


                    .emi-label {

                        margin:0;

                        font-size:
                            10px;

                        font-weight:
                            700;

                        text-transform:
                            uppercase;

                        letter-spacing:
                            .05em;

                        color:
                            #6366f1;

                    }


                    .emi-value {

                        margin:
                            5px
                            0
                            0;

                        font-size:
                            22px;

                        font-weight:
                            800;

                        color:
                            #312e81;

                    }


                    .emi-description {

                        margin:
                            4px
                            0
                            0;

                        font-size:
                            10px;

                        color:
                            #6366f1;

                    }


                    /*
                     * =================================================
                     * FOOTER
                     * =================================================
                     */

                    .footer {

                        margin-top:
                            26px;

                        text-align:
                            center;

                        font-size:
                            10px;

                        color:
                            #94a3b8;

                    }


                    /*
                     * =================================================
                     * SMALL POPUP / MOBILE
                     * =================================================
                     */

                    @media (
                        max-width:420px
                    ) {

                        .page {

                            padding:
                                14px
                                12px
                                30px;

                        }


                        .header {

                            padding-bottom:
                                16px;

                        }


                        .order-id {

                            font-size:
                                17px;

                        }


                        .summary-grid {

                            gap:
                                8px;

                        }


                        .summary-card {

                            padding:
                                11px;

                            border-radius:
                                12px;

                        }


                        .summary-value {

                            font-size:
                                11px;

                        }


                        .section-title {

                            margin-top:
                                18px;

                        }


                        .section {

                            border-radius:
                                14px;

                        }


                        .kfs-row {

                            gap:
                                12px;

                            padding:
                                11px
                                12px;

                        }


                        .kfs-label {

                            font-size:
                                10px;

                        }


                        .kfs-value {

                            max-width:
                                60%;

                            font-size:
                                10.5px;

                        }


                        .kfs-row-highlight
                        .kfs-value {

                            font-size:
                                12px;

                        }


                        .emi-card {

                            padding:
                                13px;

                            border-radius:
                                14px;

                        }


                        .emi-value {

                            font-size:
                                20px;

                        }

                    }


                    /*
                     * =================================================
                     * VERY SMALL WINDOW
                     * =================================================
                     */

                    @media (
                        max-width:340px
                    ) {

                        .summary-grid {

                            grid-template-columns:
                                1fr;

                        }


                        .kfs-row {

                            flex-direction:
                                column;

                            gap:
                                4px;

                        }


                        .kfs-value {

                            max-width:
                                100%;

                            text-align:
                                left;

                        }

                    }

                </style>

            </head>


            <body>


                <main
                    class="page"
                >


                                        <header
                        class="header"
                    >

                        <div
                            class="header-top"
                            style="display:flex;align-items:flex-start;justify-content:space-between;gap:12px;"
                        >

                            <span
                                class="tag"
                            >
                                Order Summary
                            </span>

                            <img
                                src="/CleverPeLogos/CleverPE_Black_icon.svg"
                                alt="CleverPe"
                                style="height:20px;width:20px;opacity:0.9;flex-shrink:0;"
                            />

                        </div>


                        <div
                            class="order-id"
                        >
                            OrderId : ${data.orderId}
                        </div>


                        <p
                            class="issued"
                        >
                            Issued on ${issueDate}
                        </p>

                    </header>






                    <!-- =============================================
                         CUSTOMER DETAILS
                    ============================================== -->

                    <h3
                        class="section-title"
                    >
                        Customer Details
                    </h3>


                    <section
                        class="section"
                    >

                        ${kfsRowHtml(
        "Customer Name",
        s?.customerName ?? "-"
    )}

                        ${kfsRowHtml(
        "Mobile Number",
        s?.customerMobileNo ?? "-"
    )}

                    </section>



                    <!-- =============================================
                         PARTNER DETAILS
                    ============================================== -->

                    <h3
                        class="section-title"
                    >
                        Partner Details
                    </h3>


                    <section
                        class="section"
                    >

                        ${kfsRowHtml(
        "Partner Name",
        s?.partnerName ?? "-"
    )}

                        ${kfsRowHtml(
        "Mobile Number",
        s?.partnerMobileNo ?? "-"
    )}

                    </section>



                    <!-- =============================================
                         PRODUCT DETAILS
                    ============================================== -->

                    <h3
                        class="section-title"
                    >
                        Product Details
                    </h3>


                    <section
                        class="section"
                    >

                        ${kfsRowHtml(
        "Product",
        s?.productName ??
        data.productDetails ??
        "-"
    )}


                        ${kfsRowHtml(
        "Product Amount",
        formatINR(
            data.amount
        )
    )}

                    </section>



                    <!-- =============================================
                         EMI PLAN
                    ============================================== -->

                    <h3
                        class="section-title"
                    >
                        Selected EMI Plan
                    </h3>


                    <section
    class="section"
>

    

    ${kfsRowHtml(
        "Tenure",
        `${c.NoOfNetEmis} months`
    )}

    ${kfsRowHtml(
        "Monthly EMI",
        formatINR(c.monthlyEMI),
        true
    )}

</section>


                   



                    <!-- =============================================
                         KEY FACT STATEMENT
                    ============================================== -->

                    <h3
                        class="section-title"
                    >
                        Loan Details
                    </h3>


                    <section
                        class="section"
                    >

                        ${kfsRowHtml(
        "Lender Name",
        data.lender ===
            "CAPRION"
            ? "Greshma Finvest PVT. LTD"
            : data.lender ?? "-"
    )}

                        ${kfsRowHtml(
        "Borrower Mobile",
        s?.customerMobileNo ?? "-"
    )}

                        ${kfsRowHtml(
        "Order Amount",
        formatINR(
            c.loanAmount
        )
    )}

                        ${kfsRowHtml(
        "Down Payment (incl. PF)",
        formatINR(
            c.totalDownPayment
        )
    )}

                        ${kfsRowHtml(
        "Net Loan Amount",
        formatINR(
            c.netLoanAmount
        )
    )}

                        ${kfsRowHtml(
        "Loan Tenure",
        `${c.NoOfNetEmis} months`
    )}

                        ${kfsRowHtml(
        "Other Up-front Charges",
        "₹0"
    )}

                        ${kfsRowHtml(
        "Number of Installments",
        String(
            c.NoOfNetEmis
        )
    )}

                        ${kfsRowHtml(
        "Annual Interest Rate",
        `${c.customerROI}%`
    )}

                        ${kfsRowHtml(
        "Interest Payable",
        formatINR(
            c.totalInterest
        )
    )}

                        ${kfsRowHtml(
        "Processing Fee (Including GST)",
        formatINR(
            c.totalProcessingFee
        )
    )}

                       

                        ${kfsRowHtml(
        "Repayment Frequency",
        s?.paymentFrequency ??
        "Monthly"
    )}
    ${kfsRowHtml(
        "Total Repayable Amount",
        formatINR(
            totalRepayableAmount
        ),
        true
    )}

                    </section>

                                        <h3 class="section-title">
                        Other Disclosures
                    </h3>

                    <section class="section">

                        ${kfsRowHtml(
        "Cooling off/Look-up period",
        "3 Days"
    )}

                        ${kfsRowHtml(
        "Authorized Recovery Agent",
        `Greshma Finvest PVT. LTD`
    )}

                        ${kfsRowHtml(
        "Nodal Grievance Officer Name",
        "Ms. Bindhu Kutty"
    )}

                        ${kfsRowHtml(
        "Email address",
        "grievances@caprion.in"
    )}

                    </section>



                    <!-- =============================================
                         FOOTER
                    ============================================== -->

                    <footer
                        class="footer"
                    >

                        Generated by CleverPe

                    </footer>


                </main>


            </body>

        </html>

    `;

};

type Tab = "breakdown" | "schedule";

const LoanSummary = ({
    onBack,
    onContinue,
    entryPoint = "dashboard",
    kfsUrl,
}: LoanSummaryProps) => {

    // ---------------------------------------------------------------
    // ALL hooks live here, at the top, unconditionally, in a fixed
    // order. Nothing below this block may add a new hook — any
    // useState/useEffect declared after an early `return` changes the
    // number of hooks called between renders and throws
    // "Rendered more hooks than during the previous render."
    // ---------------------------------------------------------------

    const [tab, setTab] =
        useState<Tab>("breakdown");

    const [order, setOrder] =
        useState<CurrentOrderResponse["data"] | null>(null);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState<string | null>(null);

    const [kfsLoading, setKfsLoading] =
        useState(false);

    const [kfsError, setKfsError] =
        useState<string | null>(null);

    useEffect(() => {

        const fetchOrder = async () => {

            try {

                setLoading(true);
                setError(null);

                const response =
                    await getCurrentOrder();

                const orderData =
                    response?.data;

                if (!orderData) {
                    throw new Error(
                        "Order details were not returned."
                    );
                }

                setOrder(orderData);

            } catch (error) {

                console.error(
                    "Failed to fetch order:",
                    error
                );

                setError(
                    error instanceof Error
                        ? error.message
                        : "Unable to load loan details."
                );

            } finally {

                setLoading(false);

            }
        };

        fetchOrder();

    }, []);

    /*
     * =====================================================
     * LOADING
     * =====================================================
     */

    if (loading) {
        return (
            <MobileFrame
                glowColor="#c7d2fe"
                borderColor="#e5e7eb"
                width={420}
            >
                <div className="flex h-full items-center justify-center bg-white px-6">
                    <p className="text-sm text-gray-500">
                        Loading loan details...
                    </p>
                </div>
            </MobileFrame>
        );
    }

    /*
     * =====================================================
     * ERROR
     * =====================================================
     */

    if (error || !order) {
        return (
            <MobileFrame
                glowColor="#c7d2fe"
                borderColor="#e5e7eb"
                width={420}
            >
                <div className="flex h-full items-center justify-center bg-white px-6">
                    <div className="text-center">

                        <p className="text-sm font-medium text-gray-700">
                            Loan details are unavailable.
                        </p>

                        <p className="mt-1 text-xs text-gray-400">
                            {error ??
                                "Unable to fetch your loan details."}
                        </p>

                    </div>
                </div>
            </MobileFrame>
        );
    }

    /*
     * =====================================================
     * CALCULATIONS FROM ORDER
     * =====================================================
     */

    const calculations =
        order.keyFactStatement?.calculations;

    if (!calculations) {
        return (
            <MobileFrame
                glowColor="#c7d2fe"
                borderColor="#e5e7eb"
                width={420}
            >
                <div className="flex h-full items-center justify-center bg-white px-6">
                    <div className="text-center">

                        <p className="text-sm font-medium text-gray-700">
                            Loan details are unavailable.
                        </p>

                        <p className="mt-1 text-xs text-gray-400">
                            Loan calculations are not available yet.
                        </p>

                    </div>
                </div>
            </MobileFrame>
        );
    }

    const grossAmount =
        calculations.loanAmount;

    const downpayment =
        calculations.totalDownPayment;

    const netLoanAmount =
        calculations.netLoanAmount;

    const interestCharges =
        calculations.totalInterest;

    const pfAndGst =
        calculations.totalProcessingFee;

    const numberOfEmis =
        calculations.NoOfNetEmis;

    const emiAmount =
        calculations.monthlyEMI;

    const tenureMonths =
        calculations.NoOfNetEmis;

    const grossTenureMonths =
        calculations.grossTenure;

    const advancedEmiCount =
        calculations.advancedEMI;

    const hasAdvancedEmi =
        advancedEmiCount > 0;

    const loanAmount =
        netLoanAmount;

    // EMI start date comes from keyFactStatement.calculations.emiStartDate.
    // Falls back to order.createdAt + 1 month (then "now") only if
    // emiStartDate is missing or unparsable.
    const parsedEmiStartDate = calculations.emiStartDate
        ? new Date(calculations.emiStartDate)
        : null;

    const hasValidEmiStartDate =
        parsedEmiStartDate !== null && !isNaN(parsedEmiStartDate.getTime());

    const firstEmiDate = hasValidEmiStartDate
        ? (parsedEmiStartDate as Date)
        : new Date(order.createdAt ?? Date.now());

    if (!hasValidEmiStartDate) {
        firstEmiDate.setMonth(firstEmiDate.getMonth() + 1);
    }

    // FIX: Total repayment amount was previously netLoanAmount + interestCharges only,
    // which silently dropped the processing fee (incl. GST) and didn't match the
    // KFS's "Total Repayable Amount" (netLoanAmount + totalInterest + totalProcessingFee).
    // Both now use the same formula so the numbers agree everywhere in the app.
    const totalRepaymentAmount =
        netLoanAmount + interestCharges + pfAndGst;

    const breakdownRows = [
        {
            label: "Order amount",
            value: grossAmount,
        },
        {
            label: "Down payment (incl. PF)",
            value: downpayment,
        },
        {
            label: "Net loan amount",
            value: netLoanAmount,
        },
        {
            label: "Interest charges",
            value: interestCharges,
        },
        {
            label: "Processing fee (incl. GST)",
            value: pfAndGst,
        },
    ];

    /*
     * =====================================================
     * KFS — opens as a real browser popup window (window.open
     * with fixed width/height), like a small separate window,
     * not an in-app overlay.
     *
     * The window is opened SYNCHRONOUSLY, in direct response
     * to the click, before any `await`. That's what keeps
     * browsers from treating it as a blocked popup — once you
     * `await` first and call window.open() after, most browsers
     * no longer consider it a "user gesture" and silently block
     * it (that's the "please allow pop-ups" issue from before).
     * =====================================================
     */

    const KFS_POPUP_FEATURES =
        "width=420,height=720,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes";

    const handleViewKfs = async () => {

        if (!order?.orderId) {
            return;
        }

        setKfsError(null);

        // If a direct KFS URL was passed in, just pop that URL open directly.
        if (kfsUrl) {
            const popup = window.open(
                kfsUrl,
                "kfsPopup",
                KFS_POPUP_FEATURES
            );

            if (!popup) {
                setKfsError(
                    "Your browser blocked the popup. Please allow popups for this site and try again."
                );
            }

            return;
        }

        // Open the popup window immediately (synchronously) so it isn't
        // blocked, then fill it in once the KFS data comes back.
        const popup = window.open(
            "",
            "kfsPopup",
            KFS_POPUP_FEATURES
        );

        if (!popup) {
            setKfsError(
                "Your browser blocked the popup. Please allow popups for this site and try again."
            );
            return;
        }

        popup.document.write(
            `<!doctype html><html><body style="font-family:-apple-system,Arial,sans-serif;padding:60px 20px;text-align:center;color:#64748b;"><p>Loading Order Summary...</p></body></html>`
        );

        try {

            setKfsLoading(true);

            const response =
                await getOrderKfs(
                    order.orderId
                );

            const data =
                response?.data;

            if (!data) {
                throw new Error(
                    "KFS details were not returned."
                );
            }

            popup.document.open();
            popup.document.write(buildKfsHtml(data));
            popup.document.close();

        } catch (error) {

            console.error(
                "Failed to fetch KFS:",
                error
            );

            const message =
                error instanceof Error
                    ? error.message
                    : "Unable to load Order Summary.";

            setKfsError(message);

            popup.document.open();
            popup.document.write(
                `<!doctype html><html><body style="font-family:-apple-system,Arial,sans-serif;padding:60px 20px;text-align:center;color:#ef4444;"><p>${message}</p></body></html>`
            );
            popup.document.close();

        } finally {

            setKfsLoading(false);

        }
    };

    /*
     * =====================================================
     * UI BELOW THIS POINT
     * =====================================================
     */

    return (
        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >
            <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                <div className="absolute w-64 h-64 bg-purple-200 rounded-full -top-24 -left-20 blur-3xl opacity-20" />

                <div className="absolute w-64 h-64 bg-blue-200 rounded-full -bottom-24 -right-20 blur-3xl opacity-20" />

                <div className="relative z-10 flex flex-col h-full px-6 pt-6 pb-4">

                    {/* Header */}

                    <div className="flex items-center justify-between shrink-0">

                        <div className="flex items-center gap-3">

                            {entryPoint === "dashboard" && (
                                <button
                                    onClick={onBack}
                                    className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-gray-600 shadow-sm active:scale-95 transition-transform"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                            )}

                            <p className="text-[15px] font-bold text-gray-900">
                                Loan Summary
                            </p>

                        </div>

                        <img
                            src="/CleverPeLogos/CleverPE_Black_icon.svg"
                            alt="CleverPe"
                            className="h-6 w-6 opacity-90"
                        />

                    </div>

                    {/* Hero */}

                    <div className="relative mt-5 overflow-hidden rounded-[18px] bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 shadow-[0_4px_12px_rgba(15,23,42,0.06)] shrink-0">

                        <div className="relative z-10 p-4">

                            <div className="flex items-center justify-between">

                                <div>

                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-indigo-400">
                                        Order amount
                                    </p>

                                    <p className="mt-0.5 text-[24px] font-extrabold leading-tight text-gray-900">
                                        {formatINR(loanAmount)}
                                    </p>

                                </div>

                                <div className="flex items-start gap-3">



                                    <div className="flex flex-col items-start border-l border-indigo-200/60 pl-3">
                                        <span className="text-[8.5px] font-semibold uppercase tracking-wide text-indigo-300">
                                            Net Tenure
                                        </span>
                                        <span className="text-[11px] font-bold text-gray-800">
                                            {tenureMonths} months
                                        </span>
                                    </div>



                                </div>

                            </div>

                        </div>

                        <div className="relative z-10 mx-4 border-t border-indigo-200/50" />

                        <button
                            onClick={handleViewKfs}
                            disabled={kfsLoading}
                            className="group relative z-10 flex w-full items-center justify-between px-4 py-2.5 transition-colors active:bg-white/40 hover:bg-white/30 disabled:opacity-60"
                        >
                            <div className="flex items-center gap-1.5">

                                <FileText className="h-3.5 w-3.5 text-indigo-500" />

                                <span className="text-[11.5px] font-semibold text-indigo-700">
                                    Order Summary
                                </span>

                            </div>

                            <div className="flex items-center gap-1">

                                <span className="text-[10.5px] font-semibold text-indigo-600">
                                    {kfsLoading ? "Loading..." : "View"}
                                </span>

                                <ExternalLink className="h-3.5 w-3.5 text-indigo-400" />

                            </div>

                        </button>

                        {kfsError && (
                            <p className="relative z-10 px-4 pb-2.5 text-[10.5px] font-medium text-red-500">
                                {kfsError}
                            </p>
                        )}

                    </div>

                    {/* Tabs */}

                    <div className="relative mt-6 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.03),0_10px_20px_rgba(15,23,42,0.06)] shrink-0">

                        <div className="flex items-center gap-1 border-b border-slate-100 px-2 pt-2">

                            <button
                                onClick={() =>
                                    setTab("breakdown")
                                }
                                className={`relative flex-1 flex items-center justify-center gap-1.5 rounded-t-xl py-2.5 text-[11.5px] font-semibold transition-colors ${tab === "breakdown"
                                    ? "text-indigo-600"
                                    : "text-slate-400"
                                    }`}
                            >
                                <Calculator className="h-3.5 w-3.5" />
                                Breakdown

                                {tab === "breakdown" && (
                                    <span className="absolute bottom-0 left-3 right-3 h-[2px] rounded-full bg-indigo-500" />
                                )}
                            </button>

                            <button
                                onClick={() =>
                                    setTab("schedule")
                                }
                                className={`relative flex-1 flex items-center justify-center gap-1.5 rounded-t-xl py-2.5 text-[11.5px] font-semibold transition-colors ${tab === "schedule"
                                    ? "text-indigo-600"
                                    : "text-slate-400"
                                    }`}
                            >
                                <ListChecks className="h-3.5 w-3.5" />
                                EMI Schedule

                                {tab === "schedule" && (
                                    <span className="absolute bottom-0 left-3 right-3 h-[2px] rounded-full bg-indigo-500" />
                                )}
                            </button>

                        </div>

                        {tab === "breakdown" ? (

                            <div className="px-4 py-3">

                                <div className="space-y-2">

                                    {breakdownRows.map((row) => (

                                        <div
                                            key={row.label}
                                            className="flex items-center justify-between"
                                        >

                                            <span className="text-[12px] text-gray-500">
                                                {row.label}
                                            </span>

                                            <span className="text-[12.5px] font-semibold text-gray-800">
                                                {formatINR(row.value)}
                                            </span>

                                        </div>

                                    ))}

                                </div>

                                <div className="my-2 border-t border-dashed border-slate-200" />

                                <div className="flex items-center justify-between">

                                    <span className="text-[12px] font-bold text-gray-900">
                                        Total repayment amount
                                    </span>

                                    <span className="text-[14px] font-bold bg-gradient-to-r from-indigo-500 to-blue-400 bg-clip-text text-transparent">
                                        {formatINR(totalRepaymentAmount)}
                                    </span>

                                </div>

                            </div>

                        ) : (

                            <div className="flex flex-col">

                                <div className="grid grid-cols-3 bg-slate-50 px-3.5 py-2">

                                    <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                                        EMI
                                    </p>

                                    <p className="text-center text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                                        Due Date
                                    </p>

                                    <p className="text-right text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                                        Amount
                                    </p>

                                </div>

                                <div className="emi-scroll max-h-[240px] overflow-y-auto">

                                    {Array.from({
                                        length: numberOfEmis,
                                    }).map((_, index) => (

                                        <div
                                            key={index}
                                            className={`grid grid-cols-3 items-center px-3.5 py-2.5 ${index !==
                                                numberOfEmis - 1
                                                ? "border-b border-slate-100"
                                                : ""
                                                }`}
                                        >

                                            <span className="text-[11px] font-semibold text-gray-900">
                                                {ordinal(index + 1)} EMI
                                            </span>

                                            <span className="text-center text-[11px] text-gray-700">
                                                {emiDueDateLabel(firstEmiDate, index)}
                                            </span>

                                            <span className="text-right text-[12px] font-bold text-indigo-600">
                                                {formatINR(
                                                    emiAmount
                                                )}
                                            </span>

                                        </div>

                                    ))}

                                </div>

                            </div>

                        )}

                    </div>

                    {tab === "breakdown" && (
                        <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 shrink-0">
                            <Info className="mt-[1px] h-3.5 w-3.5 shrink-0 text-amber-600" />
                            <p className="text-[9.5px] leading-snug text-amber-900">
                                <span className="font-semibold">Disclaimer:</span>{" "}
                                This summary is for information only and is not the official KFS, sanction, or loan agreement. Sanction and disbursement are at the Lender’s sole discretion. The Lender-issued KFS prevails in case of any discrepancy, and CleverPe is not liable for terms or charges set by the Lender.
                            </p>
                        </div>
                    )}

                    <div className="flex-1" />

                    {entryPoint === "mandate" && (
                        <button
                            onClick={onContinue}
                            className="w-full py-3 mt-3 mb-1 text-sm font-semibold text-white rounded-full bg-gradient-to-r from-indigo-500 to-blue-400 active:scale-[0.99] transition-transform shrink-0"
                        >
                            Continue
                        </button>
                    )}

                    <p className="mt-2 mb-1 text-center text-[11px] font-semibold tracking-wide text-gray-400 shrink-0">
                        CleverPe
                    </p>

                </div>
            </div>

        </MobileFrame>
    );
};

export default LoanSummary;